import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from 'react-query';
import { useConvertProjectMutation } from 'app/services/projects';
import { projects } from '@xmcloud/core/messages/en';
import { canConvertProject } from '@xmcloud/utils/projectHelpers';
import { IGetProjectResponseV2 } from 'app/models/projectModel';
import { UseProjectConversionProps } from 'app/models/projectConversionModel';
import { t } from '@transifex/native';
import config from 'app/config/config';
import { useProjectConversionContext } from 'app/contexts/ProjectConversionContext';
import { aptrinsicTrackEvent } from 'app/services/gainsight';
import { useGetOrganization } from 'app/services/organizations';

export const useProjectConversion = ({
	projectId,
	onModalClose,
}: UseProjectConversionProps) => {
	const [isConvertRequestSent, setIsConvertRequestSent] = useState(false);
	const toast = useToast();
	const queryClient = useQueryClient();
	const { data: organizationData } = useGetOrganization();
	const organization = organizationData?.data;
	const {
		isProjectConverted,
		isProjectConverting,
		markProjectAsConverted,
		markProjectAsConverting,
		markProjectAsNotConverting,
	} = useProjectConversionContext();

	const { mutate: convertProject, isLoading: isConverting } =
		useConvertProjectMutation({ id: projectId });

	// Reset local state when projectId changes (e.g., navigating between projects)
	useEffect(() => {
		setIsConvertRequestSent(false);
	}, [projectId]);

	const handleConvert = useCallback(() => {
		// Mark as converting immediately for both instances
		markProjectAsConverting(projectId);

		convertProject(undefined, {
			onSuccess: () => {
				// Track custom event for Gainsight Custom Events section with context
				aptrinsicTrackEvent('Convert Project Button Clicked', {
					projectId,
					action: 'convert_to_decoupled_deployment',
					timestamp: new Date().toISOString(),
					source: 'convert_project_modal',
					organizationId: organization?.id,
					organizationName: organization?.displayName,
				});

				// Mark as converted globally - this will hide it in both places
				markProjectAsConverted(projectId);
				setIsConvertRequestSent(true);
				onModalClose?.();

				// Still invalidate queries for eventual server sync
				queryClient.invalidateQueries(
					config.projects.project.queryKey(projectId),
				);
				queryClient.invalidateQueries(
					config.projects.get_projects_list_v2.queryKey,
				);

				toast({
					status: 'success',
					description: t(projects.convertSuccessMessage),
				});
			},
			onError: () => {
				// Reset converting state on error
				markProjectAsNotConverting(projectId);

				toast({
					status: 'error',
					description: t(projects.convertErrorMessage),
				});
			},
		});
	}, [
		convertProject,
		onModalClose,
		toast,
		queryClient,
		projectId,
		markProjectAsConverting,
		markProjectAsConverted,
		markProjectAsNotConverting,
		organization,
	]);

	const shouldShowConvertOption = useCallback(
		(project: IGetProjectResponseV2 | undefined) => {
			if (!project) return false;

			// Check global conversion state for the specific project
			if (
				isProjectConverted(project.id) ||
				isProjectConverting(project.id)
			) {
				return false;
			}

			// Check if project can be converted based on server data
			const serverCanConvert = canConvertProject(project);

			// If server says it can't be converted, respect that
			if (!serverCanConvert) return false;

			// Local state override - if we just sent a request for this specific project, hide it immediately
			if (project.id === projectId && isConvertRequestSent) return false;

			// Otherwise, show it if server says it's convertible
			return true;
		},
		[
			isConvertRequestSent,
			isProjectConverted,
			isProjectConverting,
			projectId,
		],
	);

	return {
		handleConvert,
		shouldShowConvertOption,
		isConverting: isConverting || isProjectConverting(projectId),
		isConvertRequestSent:
			isConvertRequestSent || isProjectConverted(projectId),
	};
};
